import { SchoolTypes } from 'src/constants';
import { TAIForm } from 'src/types';

export const emailPattern = /\S+@\S+\.\S+/;
export const passwordPattern = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

export const validateUserName = (userName: string) => {
  return userName && userName.length > 2;
};

export const validatePassword = (password: string) => {
  return password && passwordPattern.test(password);
};

export const validatePasswordMatch = (password: string, confirmPassword: string) => {
  return password === confirmPassword;
};

export const validateEmailPattern = (email: string) => {
  return emailPattern.test(email);
};

export const validateVerificationCodePattern = (code: string) => {
  return code.length >= 6;
};

export const validatePhonePattern = (phone: string) => {
  if (phone) {
    return /\+1[0-9]{3}[0-9]{3}[0-9]{4}$/.test(phone);
  } else {
    return true;
  }
};

export const positiveWholeNumber = (value: number) => value % 1 === 0 && value >= 0;
export const positiveNumber = (value: number) => value >= 0;

const checkEmpty = (value: string, min = 1) => {
  return value.length >= min;
};

export const checkAdministeredTest = (formValues: TAIForm) => {
  let valid =
    ((formValues.administeredTest.administeredTestAp === 'yes' ||
      formValues.administeredTest.administeredTestPsat === 'yes' ||
      formValues.administeredTest.administeredTestSat === 'yes' ||
      formValues.administeredTest.administeredTestOther === 'yes') &&
      formValues.administeredTest.administeredTestNone !== 'yes') ||
    (formValues.administeredTest.administeredTestAp !== 'yes' &&
      formValues.administeredTest.administeredTestPsat !== 'yes' &&
      formValues.administeredTest.administeredTestSat !== 'yes' &&
      formValues.administeredTest.administeredTestOther !== 'yes' &&
      formValues.administeredTest.administeredTestNone === 'yes');

  if (!valid) {
    return false;
  }

  if (
    formValues.administeredTest.administeredTestOther === 'yes' &&
    !checkEmpty(formValues.administeredTest.administeredTestOtherName)
  ) {
    return false;
  }

  if (
    formValues.administeredTest.administeredTestNone !== 'yes' &&
    !checkEmpty(formValues.administeredTest.administeredTestDate ?? '', 1)
  ) {
    return false;
  }

  return true;
};

export const checkSchoolType = (formValues: TAIForm) => {
  const valid =
    formValues.type.schoolTypePublic === 'yes' ||
    formValues.type.schoolTypePrivateRel === 'yes' ||
    formValues.type.schoolTypeCatholic === 'yes' ||
    formValues.type.schoolTypePrivate === 'yes' ||
    formValues.type.schoolTypeYouth === 'yes' ||
    formValues.type.schoolTypeCharter === 'yes' ||
    formValues.type.schoolTypeHome === 'yes' ||
    formValues.type.schoolTypeOnline === 'yes' ||
    formValues.type.schoolTypeDefense === 'yes' ||
    formValues.type.schoolTypeIndianAffairs === 'yes' ||
    formValues.type.schoolTypeOther === 'yes';

  if (!valid) return false;

  if (formValues.type.schoolTypeOther === 'yes' && !checkEmpty(formValues.type.schoolTypeOtherExplanation)) {
    return false;
  }

  return true;
};

export const checkHsAward = (formValues: TAIForm) => {
  if (formValues.schoolType === SchoolTypes.MS) {
    return true;
  }

  const isValid =
    formValues.hsDiploma === 'yes' ||
    formValues.grantCredit === 'yes' ||
    formValues.hsDiplomaEquivalency === 'yes' ||
    formValues.hsAwardOther === 'yes';

  if (!isValid) return false;

  if (formValues.hsAwardOther === 'yes' && !checkEmpty(formValues.hsAwardOtherText)) {
    return false;
  }

  return true;
};

export const checkAcademicDiscipline = (formValues: TAIForm) => {
  if (formValues.level !== '2') {
    return true;
  }

  const isValid =
    formValues.academicDiscipline.academicDisciplineEnglish === 'yes' ||
    formValues.academicDiscipline.academicDisciplineMatch === 'yes' ||
    formValues.academicDiscipline.academicDisciplineHistory === 'yes' ||
    formValues.academicDiscipline.academicDisciplineScience === 'yes' ||
    formValues.academicDiscipline.academicDisciplineForeignLanguage === 'yes' ||
    formValues.academicDiscipline.academicDisciplineOther === 'yes';

  if (!isValid) return false;

  if (
    formValues.academicDiscipline.academicDisciplineOther === 'yes' &&
    !checkEmpty(formValues.academicDiscipline.academicDisciplineOtherName)
  ) {
    return false;
  }

  return true;
};

export const checkPrimaryTeach = (formValues: TAIForm) => {
  if (formValues.level !== '2') {
    return true;
  }

  const isValid =
    formValues.primaryTeach.teachOnSite === 'yes' ||
    formValues.primaryTeach.teachEvening === 'yes' ||
    formValues.primaryTeach.teachHome === 'yes' ||
    formValues.primaryTeach.teachOnline === 'yes' ||
    formValues.primaryTeach.teachOther === 'yes';

  if (!isValid) {
    return false;
  }

  if (formValues.primaryTeach.teachOther === 'yes' && !checkEmpty(formValues.primaryTeach.teachOtherName)) {
    return false;
  }

  return true;
};

export const checkOnlyInternational = (formValues: TAIForm) => {
  if (
    formValues.visitingAddress.country &&
    (formValues.visitingAddress.country !== 'US' || formValues.visitingAddress.country === '')
  ) {
    return true;
  }
  return false;
};

export const checkSchoolUsedName = (formValues: TAIForm) => {
  if (!checkEmpty(formValues.usedName)) {
    return false;
  }

  if (formValues.usedName !== 'yes') {
    return true;
  }

  return checkEmpty(formValues.usedNameSchool) || checkEmpty(formValues.usedNameMergeSchools);
};

export const checkTestSecurity = (formValues: TAIForm) => {
  if (formValues.level !== '2') {
    return true;
  }

  const isValid =
    formValues.security.receivedMainOffice === 'yes' ||
    formValues.security.receivedLoadingDock === 'yes' ||
    formValues.security.receivedOther === 'yes';

  if (!isValid) {
    return false;
  }

  if (formValues.security.receivedOther === 'yes' && !checkEmpty(formValues.security.receivedOtherName)) {
    return false;
  }

  return true;
};

export const checkSchoolAccredition = (formData: TAIForm) => {
  if (formData.approvedAccreditation.cbAccredited === 'no' || formData.level === '1') {
    return true;
  }

  return !!formData.approvedAccreditation.cbAccreditedBy && !!formData.approvedAccreditation.cbAccreditedDate;
};

export const checkCollegeBoardOfferings = (formValues: TAIForm) => {

  const isValid =
      formValues.collegeBoardOfferings.preApCourses === 'yes' ||
      formValues.collegeBoardOfferings.administerPsat === 'yes' ||
      formValues.collegeBoardOfferings.satTestCenter === 'yes';

  if (!isValid) return false;

  return true;
};

export const checkSchoolFormValidation = (formData: TAIForm) => {
  if (formData.level === '2' && !checkAdministeredTest(formData)) {
    return false;
  } else if (!checkSchoolType(formData)) {
    return false;
  } else if (!checkHsAward(formData)) {
    return false;
  } else if (!checkAcademicDiscipline(formData)) {
    return false;
  } else if (!checkPrimaryTeach(formData)) {
    return false;
  } else if (!checkSchoolUsedName(formData)) {
    return false;
  } else if (!checkTestSecurity(formData)) {
    return false;
  } else if (!checkSchoolAccredition(formData)) {
    return false;
  }else if (!checkCollegeBoardOfferings(formData)) {
    return false;
  }
  else {
    return true;
  }
};